/**
 * Error Message Utilities
 * Centralized error message handling and translation
 */

export interface ErrorMapping {
  [key: string]: {
    message: string;
    type: 'error' | 'warning' | 'info';
    suggestion?: string;
  };
}

// Common error patterns and their user-friendly messages
export const ERROR_MAPPINGS: ErrorMapping = {
  // Database function errors
  'function digest(text, unknown) does not exist': {
    message: 'Database function error. Please contact technical support.',
    type: 'error',
    suggestion:
      'This appears to be a server-side database issue. Please report this to the development team.',
  },

  // Registration errors
  'register player failed': {
    message:
      'Player registration failed. Please check your input and try again.',
    type: 'warning',
    suggestion: 'Verify that all required fields are correctly filled.',
  },

  // Generic errors
  something_went_wrong: {
    message: 'An unexpected error occurred. Please try again.',
    type: 'warning',
    suggestion: 'If the problem persists, please contact support.',
  },

  // Network errors
  'network error': {
    message: 'Network connection issue. Please check your internet connection.',
    type: 'warning',
    suggestion: 'Try refreshing the page or checking your network connection.',
  },

  // Authentication errors
  unauthorized: {
    message: 'You are not authorized to perform this action.',
    type: 'error',
    suggestion: 'Please log in again or contact an administrator.',
  },

  // Validation errors
  'validation failed': {
    message: 'Invalid input data. Please check your form fields.',
    type: 'warning',
    suggestion: 'Make sure all required fields are filled correctly.',
  },
};

// Extract error details from complex error messages
export const parseErrorDetails = (
  errorMessage: string,
): {
  primary: string;
  details?: string;
  code?: string;
} => {
  // Extract error code from patterns like "ERROR: function digest(text, unknown) does not exist (SQLSTATE 42883)"
  const sqlStateMatch = errorMessage.match(/\(SQLSTATE (\w+)\)/);
  const errorCodeMatch = errorMessage.match(/\[([^\]]+)\]/);

  // Extract the main error message
  let primary = errorMessage;
  let details: string | undefined;
  let code: string | undefined;

  if (sqlStateMatch) {
    code = sqlStateMatch[1];
    primary = errorMessage.replace(/\(SQLSTATE \w+\)/, '').trim();
  }

  if (errorCodeMatch) {
    code = code || errorCodeMatch[1];
    primary = primary.replace(/\[[^\]]+\]/, '').trim();
  }

  // Split primary message and details if there's a colon separator
  const colonIndex = primary.indexOf(':');
  if (colonIndex > 0) {
    details = primary.substring(colonIndex + 1).trim();
    primary = primary.substring(0, colonIndex).trim();
  }

  return { primary, details, code };
};

// Get user-friendly error message based on error patterns
export const getUserFriendlyError = (
  errorMessage: string,
): {
  message: string;
  type: 'error' | 'warning' | 'info';
  suggestion?: string;
} => {
  const lowerCaseError = errorMessage.toLowerCase();

  // Check for exact matches first
  for (const [pattern, mapping] of Object.entries(ERROR_MAPPINGS)) {
    if (errorMessage.includes(pattern)) {
      return mapping;
    }
  }

  // Check for pattern matches
  if (
    lowerCaseError.includes('duplicate') ||
    lowerCaseError.includes('already exists')
  ) {
    return {
      message: 'This record already exists.',
      type: 'warning',
      suggestion:
        'Please use a different value or check if the record already exists.',
    };
  }

  if (
    lowerCaseError.includes('timeout') ||
    lowerCaseError.includes('timed out')
  ) {
    return {
      message: 'Request timed out. Please try again.',
      type: 'warning',
      suggestion: 'The operation took too long. Please try again.',
    };
  }

  if (
    lowerCaseError.includes('permission') ||
    lowerCaseError.includes('forbidden')
  ) {
    return {
      message: 'You do not have permission to perform this action.',
      type: 'error',
      suggestion: 'Contact your administrator if you believe this is an error.',
    };
  }

  // Default fallback
  console.log(errorMessage)
  return {
    message: 'An unexpected error occurred. Please try again.',
    type: 'error',
    suggestion: 'If the problem persists, please contact technical support.',
  };
};
